import {WindowSizeConsumer} from '@albert-io/atomic';
import Card from '@albert-io/atomic/atoms/Card/Card.react';
import cx from 'classnames';
import React, {KeyboardEvent, useRef} from 'react';
import {Popper} from 'react-popper';
import {useMenu} from './MenuContext.react';

const modifiers = {
  preventOverflow: {
    padding: 64
  },
  flip: {
    padding: 16
  },
  setMobile: {
    enabled: false
  }
};

interface MenuContentProps extends PropsWithChildrenRequired, PropsWithClassNameOptional {}

const MenuContent = ({children, className = ''}: MenuContentProps) => {
  const {isOpen, close, position, positionFixed, popperProps} = useMenu();
  const menuRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!menuRef.current) return;

    const menuItems = Array.from(
      menuRef.current.querySelectorAll('[role="menuitem"]:not([tabindex^="-"])')
    ) as HTMLElement[];

    const currentIndex = menuItems.findIndex((item) => item === document.activeElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % menuItems.length;
        menuItems[nextIndex]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = currentIndex <= 0 ? menuItems.length - 1 : currentIndex - 1;
        menuItems[prevIndex]?.focus();
        break;
      }
      case 'Escape': {
        e.preventDefault();
        close();
        break;
      }
      default:
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <WindowSizeConsumer>
      {(context) => {
        if (!context) return null;

        const {viewportWidth, breakpoints} = context;
        const isMobile = viewportWidth <= breakpoints.s;

        // Update mobile modifier
        modifiers.setMobile.enabled = isMobile;

        return (
          <Popper
            className={cx('o-menu-content__wrapper', className, {
              'o-menu-content__wrapper--show': isOpen
            })}
            modifiers={modifiers}
            placement={position}
            positionFixed={positionFixed}
            {...popperProps}
          >
            <Card
              paddingLevel={1}
              // @ts-expect-error role is a valid prop on Card since it's a div wrapper
              role='menu'
              shadow='close'
              className='o-menu-content'
              onKeyDown={handleKeyDown}
              ref={menuRef}
            >
              {children}
            </Card>
          </Popper>
        );
      }}
    </WindowSizeConsumer>
  );
};

export default MenuContent;
